import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { processLegalReferences } from '@/lib/legalReferences';
import { ExternalLink } from 'lucide-react';
import { useMemo } from 'react';

interface LegalMarkdownProps {
  content: string;
  className?: string;
}

/**
 * Markdown component that automatically detects and links legal references
 * (BOE, laws, court decisions, EU regulations)
 */
export function LegalMarkdown({ content, className }: LegalMarkdownProps) {
  // Process content to add legal reference links
  const processedContent = useMemo(() => {
    return processLegalReferences(content);
  }, [content]);

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Custom link renderer for legal references
          a: ({ href, children, ...props }) => {
            const isLegalLink = href && (
              href.includes('boe.es') ||
              href.includes('poderjudicial.es') ||
              href.includes('tribunalconstitucional.es') ||
              href.includes('eur-lex.europa.eu') ||
              href.includes('doe.juntaex.es') ||
              href.includes('juntadeandalucia.es')
            );

            if (isLegalLink) {
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 text-[#06D3AA] hover:text-[#05b897] underline decoration-[#06D3AA]/30 hover:decoration-[#06D3AA] transition-colors font-medium"
                  title="Abrir fuente legal oficial"
                  {...props}
                >
                  {children}
                  <ExternalLink className="h-3 w-3 inline-block ml-0.5 opacity-60" />
                </a>
              );
            }

            // Regular external links
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#002B5B] hover:text-[#06D3AA] underline transition-colors"
                {...props}
              >
                {children}
              </a>
            );
          },
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
}
